import { useEffect, useState } from 'react'

import { Checkbox } from '@/app/[locale]/(ui)/checkbox'
import { Alert } from '@/app/[locale]/(ui)/alert'
import { LoaderCircle } from 'lucide-react'
import { useTranslations } from 'next-intl'

export type CouponStatusType = (typeof CouponStatus)[keyof typeof CouponStatus]

export const Coupon = ({
  coupon,
  setCoupon,
  startExpanded = false,
  couponStatus = 'NOT_CHECKED',
  isValidating = false,
}: {
  coupon: string
  setCoupon: (coupon: string) => void
  withDivider?: boolean
  startExpanded?: boolean
  couponStatus?: CouponStatusType
  isValidating?: boolean
}) => {
  const t = useTranslations()
  const [isExpanded, setIsExpanded] = useState(startExpanded)

  useEffect(() => {
    setIsExpanded(startExpanded)
  }, [])

  return (
    <div className="w-full bg-white">
      <div className="flex items-center">
        <Checkbox
          label={t('Have_discount_code')}
          checked={isExpanded}
          onChange={(checked) => {
            setIsExpanded(checked)
            if (!checked) {
              setCoupon('')
            }
          }}
        />
      </div>
      {isExpanded && (
        <div className="overflow-hidden">
          <div className="mt-[10px]">
            <div className="mt-[10px]">
              <label htmlFor="coupon-input-field" className="mb-1 block">
                <span className="font-['PT_Sans'] text-[12px] font-bold text-black">{t('Discount_code')}</span>
              </label>
              <div className="relative w-full">
                <input
                  id="coupon-input-field"
                  defaultValue={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  className="box-border w-full rounded-md border border-black bg-white px-4 py-3 pr-10 text-[16px] leading-none text-black"
                />
                {isValidating && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <LoaderCircle className="animate-spin" />
                  </div>
                )}
              </div>
            </div>
            {couponStatus !== CouponStatus.NOT_CHECKED && !isValidating ? (
              <div className="mt-2">
                {couponStatus === CouponStatus.VALID ? (
                  <Alert variant="success">{t('Discount_code_success')}</Alert>
                ) : couponStatus === CouponStatus.INVALID ? (
                  <Alert variant="error" className="flex items-center gap-2">
                    {t('Discount_code_invalid')}
                  </Alert>
                ) : couponStatus === CouponStatus.UNABLE_TO_CHECK ? (
                  <Alert variant="error" className="flex items-center gap-2">
                    {t('Discount_code_unable_to_check')}
                  </Alert>
                ) : couponStatus === CouponStatus.EXPIRED ? (
                  <Alert variant="error" className="flex items-center gap-2">
                    {t('Discount_code_expired')}
                  </Alert>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}

export const CouponStatus = {
  NOT_CHECKED: 'NOT_CHECKED',
  VALID: 'VALID',
  INVALID: 'INVALID',
  EXPIRED: 'EXPIRED',
  UNABLE_TO_CHECK: 'UNABLE_TO_CHECK',
} as const
